import React from "react";
import { Loader2, Sparkles } from "lucide-react";
import { TYPE_COLORS, TYPE_LABELS } from "@/lib/api";

export const Eyebrow = ({ children, className = "" }: any) => <p className={`eyebrow ${className}`}>{children}</p>;

export const PageHeader = ({ eyebrow, title, subtitle, action, testId }: any) => (
  <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-8" data-testid={testId}>
    <div>
      {eyebrow && <Eyebrow className="mb-2">{eyebrow}</Eyebrow>}
      <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{title}</h1>
      {subtitle && <p className="text-ink-muted mt-2 max-w-2xl text-sm sm:text-base">{subtitle}</p>}
    </div>
    {action && <div className="flex gap-2 shrink-0">{action}</div>}
  </div>
);

export const TypeChip = ({ type }: { type: string }) => (
  <span className={`chip ${TYPE_COLORS[type] || "bg-sand text-ink-muted"}`} data-testid={`type-chip-${type}`}>{TYPE_LABELS[type] || type}</span>
);

const VOICE: Record<string, { label: string; cls: string; hint: string }> = {
  learning: { label: "Voice Match: Learning", cls: "bg-[#E5E8A7] text-[#687000] border-[#DBD7CD]", hint: "Add more of your real writing and Email Bestie will sound more like you." },
  good: { label: "Voice Match: Good", cls: "bg-[#E5E8A7] text-[#071D2B] border-[#DBD7CD]", hint: "Solid match. A few more samples make it stronger." },
  strong: { label: "Voice Match: Strong", cls: "bg-olive-light text-olive-deep border-[#DBD7CD]", hint: "Email Bestie has a strong read on how you write." },
};

export const VoiceBadge = ({ level = "learning", showHint = false, size = "md" }: { level?: string; showHint?: boolean; size?: "sm" | "md" | "lg" }) => {
  const v = VOICE[level] || VOICE.learning;
  const pad = size === "lg" ? "px-4 py-2 text-sm" : size === "sm" ? "px-2 py-0.5 text-[11px]" : "px-3 py-1 text-xs";
  return (
    <div className="inline-flex flex-col gap-1">
      <span className={`inline-flex items-center gap-1.5 rounded-full border font-semibold ${pad} ${v.cls}`} data-testid={`voice-badge-${level}`}>
        <Sparkles className="size-3.5" /> {v.label}
      </span>
      {showHint && <span className="text-xs text-ink-soft">{v.hint}</span>}
    </div>
  );
};

export const EmptyState = ({ title, body, action, testId }: any) => (
  <div className="card-soft p-10 text-center" data-testid={testId}>
    <h3 className="font-serif text-2xl text-ink">{title}</h3>
    {body && <p className="text-ink-muted mt-2 text-sm max-w-md mx-auto">{body}</p>}
    {action && <div className="mt-6 flex justify-center">{action}</div>}
  </div>
);

export const Busy = ({ label = "Thinking..." }: { label?: string }) => (
  <span className="inline-flex items-center gap-2"><Loader2 className="size-4 animate-spin" /> {label}</span>
);

export const Field = ({ label, hint, children, testId }: any) => (
  <div data-testid={testId}>
    <label className="label">{label}</label>
    {children}
    {hint && <p className="hint">{hint}</p>}
  </div>
);

export const WhyNote = ({ children, testId }: any) => (
  <p className="text-sm text-ink-muted border-l-2 border-olive pl-3" data-testid={testId}><span className="font-semibold text-olive-deep">Why: </span>{children}</p>
);
